import { memo, useCallback, useRef } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "redux/store"
import { get } from "lodash"
import { Button, DataGrid } from "devextreme-react"
import {
  Item,
  Pager,
  Paging,
  Column,
  Toolbar,
  FilterRow,
  SearchPanel,
} from "devextreme-react/data-grid"
import {
  resetYears,
  expenditureProfileSelector,
  setSelectedExpenditureProject,
  setSelectedExpenditureProjectData,
} from "redux/expenditure-profile/expenditureProfileSlice"
import { expenditureProfileDateCellVal, onExpenditureProfileCellPrepared } from "./utils"

const ExpenseProfileTable = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const dataGridRef = useRef<DataGrid>(null)

  const { expenditureProfileProjects, selectedExpenditureProjectData, expenditureReportData } =
    useSelector(expenditureProfileSelector)

  const onRowClick = useCallback(
    (e: any) => {
      const estimate = get(e, "data")
      const estimateID = get(e, "data.PROJECT_ID")
      const selectedEstimateID = get(selectedExpenditureProjectData, "PROJECT_ID")
      const reportGenerated = get(e, "data.PROJECT_REPORT_GENERATED") === "Y"

      if (reportGenerated) {
        if (selectedEstimateID !== estimateID || !expenditureReportData.length) {
          dispatch(setSelectedExpenditureProjectData())
          dispatch(resetYears())
        }

        dispatch(setSelectedExpenditureProject(estimate))
        navigate(`/carbonExpenditureProfile/${estimateID}/report`)
      }
    },
    [selectedExpenditureProjectData]
  )

  return (
    <DataGrid
      id="expenditure-profile-container"
      ref={dataGridRef}
      dataSource={expenditureProfileProjects}
      showBorders
      showColumnLines
      columnAutoWidth
      hoverStateEnabled
      allowColumnResizing
      allowColumnReordering
      onRowClick={onRowClick}
      onCellPrepared={(e) => onExpenditureProfileCellPrepared(e, t)}
      stateStoring={{
        enabled: true,
        type: "localStorage",
        storageKey: "expenditure-profile-grid-state",
        savingTimeout: 0,
      }}
    >
      <FilterRow visible />
      <Paging defaultPageSize={10} />
      <SearchPanel visible width={240} />
      <Pager showPageSizeSelector showInfo visible />
      <Toolbar>
        <Item location="before">
          <span title={t("toolbarActions.resetAll")}>
            <Button
              icon="refresh"
              stylingMode="text"
              onClick={() => dataGridRef.current?.instance.state(null)}
            />
          </span>
        </Item>
        <Item name="searchPanel" />
      </Toolbar>
      <Column
        caption={t("carbExpProfile.schemeTitle")}
        dataField="PROJ_COMP_DESC"
        dataType="string"
      />
      <Column
        caption={t("carbExpProfile.estimateNo")}
        dataField="PROJECT_QUOTE_NO"
        dataType="string"
      />
      <Column
        caption={t("carbExpProfile.estimateDesc")}
        dataField="PROJECT_TITLE"
        dataType="string"
      />
      <Column
        caption={t("carbExpProfile.estimateStage")}
        dataField="PROJECT_EST_STAGE"
        dataType="string"
      />
      <Column
        caption={t("carbExpProfile.estimateDate")}
        dataField="PROJECT_DATE"
        dataType="string"
        calculateCellValue={expenditureProfileDateCellVal}
      />
      <Column
        caption={t("carbExpProfile.estimator")}
        dataField="ESTIMATOR_NAME"
        dataType="string"
      />
    </DataGrid>
  )
}

export default memo(ExpenseProfileTable)
